import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/services/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { GlobalHeader } from '@/components/GlobalHeader'
import toast from 'react-hot-toast'

export function ResetPasswordRequestPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmed = email.trim()
    if (!trimmed) {
      setError(t('auth.emailRequired', 'Email is required'))
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError(t('auth.invalidEmail', 'Invalid email address'))
      return
    }

    setIsLoading(true)
    try {
      await authApi.requestPasswordReset(trimmed)
      setSent(true)
      toast.success(t('auth.resetLinkSent', 'If this email is registered, you will receive a reset link.'))
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || t('auth.resetRequestError', 'Something went wrong. Please try again.')
      setError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background-light to-secondary/5">
      <GlobalHeader showNavigation={false} />
      <div className="flex items-center justify-center p-4 pt-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <img
              src="/assets/djelo-sem-fundo.png"
              alt="Djelo"
              className="w-40 h-auto object-contain mx-auto mb-4"
            />
            <p className="text-text-secondary">
              {t('common.walletDescription', 'Sua carteira digital em São Tomé e Príncipe')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-text-primary mb-2 text-center">
              {t('auth.forgotPassword', 'Forgot password?')}
            </h2>
            <p className="text-text-secondary text-sm text-center mb-6">
              {t('auth.forgotPasswordDescription', 'Enter your email and we will send you a link to reset your password.')}
            </p>

            {sent ? (
              <div className="text-center space-y-4">
                <p className="text-text-primary">
                  {t('auth.checkYourEmail', 'Check your email. If this account exists, you will receive a link to reset your password.')}
                </p>
                <Link to="/login" className="text-primary font-medium hover:underline block">
                  {t('auth.backToLogin', 'Back to login')}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder={t('auth.emailPlaceholder', 'Email')}
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  error={error || undefined}
                  required
                  className="text-lg"
                />
                <Button
                  type="submit"
                  className="w-full mobile-button"
                  disabled={isLoading}
                  loading={isLoading}
                >
                  {isLoading ? t('common.loading') : t('auth.sendResetLink', 'Send reset link')}
                </Button>
              </form>
            )}

            {!sent && (
              <p className="mt-6 text-center text-sm text-text-secondary">
                <Link to="/login" className="text-primary font-medium hover:underline">
                  {t('auth.backToLogin', 'Back to login')}
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
